import { useEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from 'react';
import { Link } from 'react-router';
import {
  buildBracketTree,
  classifyLiveSlot,
  isMatchLive,
  liveMatchesByTeam,
  liveMinuteLabel,
  lockedR32SlotTeamIds,
  resolveBracket,
  type BracketTree,
  type MatchPublic,
  type MatchStage,
  type TeamPublic,
} from '@mondy/shared';
import type { Group } from '@/components/GroupStandings';
import { LockBadge } from '@/components/LockBadge';
import { TeamFlag } from '@/components/TeamFlag';
import { cn } from '@/lib/cn';
import { formatKickoffDayMonth, formatKickoffShort, isKickoffToday } from '@/lib/format';

// ============================================================================
// Two presentations of the same bracket, picked at the 1920px (1080p) breakpoint:
//   • Desktop — a single two-sided converging bracket (BBC style): the left half
//     flows R32 → SF rightward, the right half is x-mirrored and flows leftward,
//     and the Final sits in the center column with the third-place playoff below
//     it. The fixed-geometry canvas is CSS-scaled to fill the panel width.
//   • Mobile — a pager (‹ rectangles › + swipe) over R16/QF/SF/Final. Each
//     round shows the two halves head-to-head, every match fed by its
//     previous-round games (with score) via short connector elbows — so R16
//     already shows the R32 matchups and needs no separate tab. The Final sits
//     between its two semis.
// ============================================================================

// ---- Desktop geometry: a fixed design space scaled to the panel (never
// measured per-node). Both halves span the same 8 leaf rows; the two semis and
// the Final all center at row 3.5. ------------------------------------------
const NODE_W = 140;
const NODE_H = 50;
const COL_GAP = 26; // gap between columns within a half
const ROW_PITCH = 62; // vertical pitch between adjacent R32 leaves
const TOP_PAD = 26;
const HALF_W = 4 * NODE_W + 3 * COL_GAP; // R32,R16,QF,SF columns
const CENTER_GAP = 30; // SF ↔ Final gap
const FINAL_X = HALF_W + CENTER_GAP; // center column left edge
const BASE_W = FINAL_X + NODE_W + CENTER_GAP + HALF_W; // + the mirrored right half
const BASE_H = TOP_PAD + 8 * ROW_PITCH + 20; // 8 leaf rows per half

// Scale floor: below this the bracket scrolls horizontally rather than shrink
// into illegibility; above the ceiling it stops growing and centers.
const MIN_SCALE = 0.5;
const MAX_SCALE = 1.3;

const STAGE_COL: Partial<Record<MatchStage, number>> = {
  round_of_32: 0,
  round_of_16: 1,
  quarter: 2,
  semi: 3,
};

// Compact column labels, repeated on both sides of the converging tree.
const COL_LABELS = ['R32', 'R16', 'QF', 'SF'];

// Each round is its own pager screen (the previous round is NOT shown as feeders
// on narrow viewports), so R32 gets its own tab. Five rounds → five dots.
const ROUND_TABS: { stage: MatchStage; label: string }[] = [
  { stage: 'round_of_32', label: 'R32' },
  { stage: 'round_of_16', label: 'R16' },
  { stage: 'quarter', label: 'QF' },
  { stage: 'semi', label: 'SF' },
  { stage: 'final', label: 'Final' },
];

// The round each narrow-web screen's winners advance into — written on the
// central rail so every card's connector line reads as "these feed into <next>".
// (The semifinals are a special two-outcome screen — see SemiScreen.)
const NEXT_STAGE_LABEL: Partial<Record<MatchStage, string>> = {
  round_of_32: 'R16',
  round_of_16: 'QF',
  quarter: 'SF',
};

// Left-edge x for a card at (side, column-within-half). The right half mirrors
// the left: its column 0 (R32) is the far right edge.
function colX(side: 'left' | 'right' | 'center', col: number): number {
  if (side === 'center') return FINAL_X;
  if (side === 'left') return col * (NODE_W + COL_GAP);
  return FINAL_X + NODE_W + CENTER_GAP + (3 - col) * (NODE_W + COL_GAP);
}
const slotCenterY = (slot: number) => TOP_PAD + slot * ROW_PITCH + ROW_PITCH / 2;

// ---- Shared node rendering --------------------------------------------------

// An idle slot: flag + 3-char code. The full name and "Winner …" placeholders
// are dropped — the bracket graph already shows who plays whom, so the code
// alone keeps it compact.
function Slot({ team, locked }: { team: TeamPublic | null; locked?: boolean | null }) {
  // Certainty badge: shown only for a projected R32 team during the group stage
  // (locked is a boolean). null ⟹ either an unresolved R16+ slot or the
  // all-groups-finished state — render nothing.
  const showLock = locked !== null && locked !== undefined && !!team;
  return (
    <div className={cn('ko-slot', !team && 'unresolved')}>
      <TeamFlag code={team?.fifaCode ?? null} url={team?.flagUrl ?? null} size="xs" mute />
      <span className="ko-code">{team?.fifaCode ?? '—'}</span>
      {showLock ? <LockBadge locked={!!locked} /> : null}
    </div>
  );
}

// One team's identity line inside a resolved card: flag + code (mirrors the idle
// Slot, so a live/finished card keeps the same flags the bracket shows at rest).
function TeamLine({ team }: { team: TeamPublic | null }) {
  return (
    <div className="ko-rrow">
      <TeamFlag code={team?.fifaCode ?? null} url={team?.flagUrl ?? null} size="xs" mute />
      <span className="ko-code">{team?.fifaCode ?? '—'}</span>
    </div>
  );
}

// One team's score cell: the main (ET-or-FT) goals, plus the shootout tally in
// brackets ONLY when the match was decided on penalties.
function ScoreCell({ goals, pens }: { goals: number; pens: number | null }) {
  return (
    <div className="ko-rsc">
      <span className="ko-sc">{goals}</span>
      {pens !== null ? <span className="ko-pen">({pens})</span> : null}
    </div>
  );
}

// A resolved match (live or finished): two team rows on one side (flag + code),
// the status/minute centered in the middle, and each team's score stacked on the
// other side. The desktop right half mirrors this (scores inner, flag outer).
//   🇲🇽 MEX   62'   2 (4)
//   🇿🇦 RSA          2 (2)
function ResultCard({
  team1,
  team2,
  main,
  pens,
  label,
  live,
}: {
  team1: TeamPublic | null;
  team2: TeamPublic | null;
  main: { team1: number; team2: number };
  pens: { team1: number; team2: number } | null;
  label: string;
  live: boolean;
}) {
  return (
    <div className={cn('ko-result', live && 'live')}>
      <div className="ko-rteams">
        <TeamLine team={team1} />
        <TeamLine team={team2} />
      </div>
      {label ? <div className="ko-rmid">{label}</div> : null}
      <div className="ko-rscores">
        <ScoreCell goals={main.team1} pens={pens?.team1 ?? null} />
        <ScoreCell goals={main.team2} pens={pens?.team2 ?? null} />
      </div>
    </div>
  );
}

// A not-yet-started card's kickoff: localized "Today" (or "DD/MM") over "HH:MM".
// Sits on the inner/center side of the idle card, mirroring where a played card
// shows its score.
function KickoffBlock({ iso }: { iso: string }) {
  return (
    <div className="ko-kick">
      <span className="ko-kick-day">{isKickoffToday(iso) ? 'Today' : formatKickoffDayMonth(iso)}</span>
      <span className="ko-kick-time">{formatKickoffShort(iso)}</span>
    </div>
  );
}

function Node({
  m,
  team1,
  team2,
  liveByTeam,
  lockedIds,
}: {
  m: MatchPublic;
  team1: TeamPublic | null;
  team2: TeamPublic | null;
  liveByTeam: Map<string, MatchPublic>;
  lockedIds: Set<string> | null;
}) {
  const live1 = classifyLiveSlot(team1, m.id, liveByTeam);
  const live2 = classifyLiveSlot(team2, m.id, liveByTeam);
  const ownLive = isMatchLive(m) && (live1.state === 'own' || live2.state === 'own');
  const finished = m.status === 'finished' && !!m.result;
  const main = m.result ? (m.result.extraTime ?? m.result.fullTime) : null;
  const compact = (ownLive || finished) && main !== null;
  const scheduled = m.status === 'scheduled';

  // Middle label: live → the minute (the green tint already reads "live");
  // paused → a SHORT break tag keyed off the preserved minute ("PENS" past 120'
  // awaiting the shootout, "ET" from 90' on for the FT→ET break / ET half-time /
  // ET play, else "HT") — matchPausedLabel's prose ("AWAITING EXTRA TIME",
  // "AWAITING PENALTIES") is far too wide for this narrow zone; finished → AET
  // once it ran past 90 (penalties imply extra time too), else FT. The shootout
  // tally lives in the score brackets, not here.
  let label = '';
  if (ownLive)
    label =
      m.status === 'paused'
        ? (m.minute ?? 0) >= 120
          ? 'PENS'
          : (m.minute ?? 0) >= 90
            ? 'ET'
            : 'HT'
        : (liveMinuteLabel(m.minute, m.addedTime) ?? 'LIVE');
  else if (finished) label = m.result?.extraTime || m.result?.penalties ? 'AET' : 'FT';

  return (
    <Link to={`/matches/${m.id}`} className={cn('ko-node', ownLive && 'live')}>
      {compact && main ? (
        <ResultCard
          team1={team1}
          team2={team2}
          main={main}
          pens={m.result?.penalties ?? null}
          label={label}
          live={ownLive}
        />
      ) : scheduled ? (
        <div className="ko-idle">
          <div className="ko-iteams">
            <Slot team={team1} locked={lockedIds ? (team1 ? lockedIds.has(team1.id) : null) : null} />
            <Slot team={team2} locked={lockedIds ? (team2 ? lockedIds.has(team2.id) : null) : null} />
          </div>
          <KickoffBlock iso={m.kickoffUtc} />
        </div>
      ) : (
        <>
          <Slot team={team1} locked={lockedIds ? (team1 ? lockedIds.has(team1.id) : null) : null} />
          <Slot team={team2} locked={lockedIds ? (team2 ? lockedIds.has(team2.id) : null) : null} />
        </>
      )}
    </Link>
  );
}

function Crown() {
  return (
    <svg className="ko-crown" viewBox="0 0 24 16" width="22" height="15" aria-hidden>
      <path
        d="M3 14 L3 4 L8 9 L12 2 L16 9 L21 4 L21 14 Z"
        fill="currentColor"
        stroke="rgba(0, 0, 0, 0.35)"
        strokeWidth="0.75"
        strokeLinejoin="round"
      />
    </svg>
  );
}

type NodeRenderer = (m: MatchPublic) => ReactNode;

// ---- Desktop: a single two-sided converging bracket ------------------------

interface Placed {
  match: MatchPublic;
  x: number;
  cy: number;
  mirror: boolean;
}
interface DesktopLayout {
  nodes: Placed[];
  headers: { x: number; label: string }[];
  finalId: string | null;
  thirdId: string | null;
  connectors: string[];
  baseH: number;
  ok: boolean;
}

function computeDesktopLayout(tree: BracketTree, byId: Map<string, MatchPublic>): DesktopLayout {
  const placed = new Map<string, Placed>();
  const finalMatch = tree.rounds.find((r) => r.stage === 'final')?.matches[0];
  const sfs = finalMatch ? (tree.feedersOf.get(finalMatch.id) ?? []) : [];
  if (!finalMatch || sfs.length !== 2) {
    return { nodes: [], headers: [], finalId: null, thirdId: null, connectors: [], baseH: 0, ok: false };
  }

  // One half: leaves get sequential vertical rows, internal nodes center on
  // their feeders. Both halves re-index from row 0 so they share the band and
  // the two semis (and the Final) center.
  function placeHalf(rootId: string, side: 'left' | 'right') {
    let leaf = 0;
    const walk = (id: string): number => {
      const m = byId.get(id);
      if (!m) return 0;
      const feeders = tree.feedersOf.get(id) ?? [];
      const slot =
        feeders.length === 0
          ? leaf++
          : feeders.map(walk).reduce((a, b) => a + b, 0) / feeders.length;
      placed.set(id, {
        match: m,
        x: colX(side, STAGE_COL[m.stage] ?? 0),
        cy: slotCenterY(slot),
        mirror: side === 'right',
      });
      return slot;
    };
    walk(rootId);
  }

  placeHalf(sfs[0]!, 'left');
  placeHalf(sfs[1]!, 'right');

  const finalCy = (placed.get(sfs[0]!)!.cy + placed.get(sfs[1]!)!.cy) / 2;
  placed.set(finalMatch.id, { match: finalMatch, x: FINAL_X, cy: finalCy, mirror: false });
  if (tree.thirdPlace)
    placed.set(tree.thirdPlace.id, {
      match: tree.thirdPlace,
      x: FINAL_X,
      cy: finalCy + 2 * ROW_PITCH,
      mirror: false,
    });

  // Each feeder elbows into its parent: out the edge facing the parent, across
  // to the midpoint, down/up to the parent's center, into the parent edge. The
  // Final's two semis approach from opposite sides; the third-place match shares
  // those semis, so its lines fork downward off the same horizontal stub.
  const connectors: string[] = [];
  for (const { match, x, cy } of placed.values()) {
    for (const fId of tree.feedersOf.get(match.id) ?? []) {
      const child = placed.get(fId);
      if (!child) continue;
      const childOnLeft = child.x < x;
      const childEdge = childOnLeft ? child.x + NODE_W : child.x;
      const parentEdge = childOnLeft ? x : x + NODE_W;
      const midX = (childEdge + parentEdge) / 2;
      connectors.push(`M ${childEdge} ${child.cy} H ${midX} V ${cy} H ${parentEdge}`);
    }
  }

  const headers = [
    ...COL_LABELS.map((label, i) => ({ x: colX('left', i), label })),
    { x: FINAL_X, label: 'Final' },
    ...COL_LABELS.map((label, i) => ({ x: colX('right', i), label })),
  ];

  return {
    nodes: [...placed.values()],
    headers,
    finalId: finalMatch.id,
    thirdId: tree.thirdPlace?.id ?? null,
    connectors,
    baseH: BASE_H,
    ok: true,
  };
}

// Scale the fixed-geometry canvas to fill the panel width. CSS can't turn a
// `cqw` ratio into the unitless number `transform: scale()` needs, so we
// measure the container and clamp the scale (below MIN_SCALE it stays readable
// and scrolls horizontally; above MAX_SCALE it stops growing and centers).
function useFitScale(baseW: number): [RefObject<HTMLDivElement | null>, number] {
  const ref = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const apply = () => setScale(Math.min(MAX_SCALE, Math.max(MIN_SCALE, el.clientWidth / baseW)));
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    apply();
    return () => ro.disconnect();
  }, [baseW]);
  return [ref, scale];
}

function DesktopBracket({ layout, node }: { layout: DesktopLayout; node: NodeRenderer }) {
  const [ref, scale] = useFitScale(BASE_W);
  return (
    <div className="ko-fit" ref={ref}>
      <div
        className="ko-scale-wrap"
        style={{ width: BASE_W * scale, height: layout.baseH * scale }}
      >
        <div
          className="ko-canvas"
          style={{ width: BASE_W, height: layout.baseH, transform: `scale(${scale})` }}
        >
          <svg className="ko-lines" width={BASE_W} height={layout.baseH} aria-hidden>
            {layout.connectors.map((d, i) => (
              <path key={i} d={d} fill="none" stroke="var(--line)" strokeWidth={1.5} />
            ))}
          </svg>
          {layout.headers.map((h, i) => (
            <div key={i} className="ko-header" style={{ left: h.x, width: NODE_W }}>
              {h.label}
            </div>
          ))}
          {layout.nodes.map((n) => (
            <div
              key={n.match.id}
              className={cn('ko-pos', n.mirror && 'mirror')}
              style={{ left: n.x, top: n.cy - NODE_H / 2, width: NODE_W, height: NODE_H }}
            >
              {n.match.id === layout.finalId ? <Crown /> : null}
              {n.match.id === layout.thirdId ? <div className="ko-pin">Third place</div> : null}
              {node(n.match)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---- Mobile: round tabs; one round per screen (no previous-round feeders) ----

// Split the bracket into its two halves and assign each node a pre-order DFS
// rank within its half. Sorting a round's matches by this rank (rather than by
// matchNumber) puts true bracket-siblings adjacent, so RoundScreen's ⊐ joins the
// pair that actually feeds the same next-round match. R32's matchNumber order is
// NOT its bracket order (its games are re-keyed to FIFA numbering in
// buildOrdinalId), so a matchNumber sort mis-paired the R32 screen.
function halfLayout(tree: BracketTree): {
  left: Set<string>;
  right: Set<string>;
  rank: Map<string, number>;
} {
  const finalMatch = tree.rounds.find((r) => r.stage === 'final')?.matches[0];
  const sfs = finalMatch ? (tree.feedersOf.get(finalMatch.id) ?? []) : [];
  const rank = new Map<string, number>();
  let order = 0;
  const collect = (id: string | undefined, into: Set<string>) => {
    if (!id) return;
    into.add(id);
    rank.set(id, order++);
    for (const f of tree.feedersOf.get(id) ?? []) collect(f, into);
  };
  const left = new Set<string>();
  const right = new Set<string>();
  collect(sfs[0], left);
  collect(sfs[1], right);
  return { left, right, rank };
}

// The Final (crowned) above the third-place playoff. The semifinals are NOT
// repeated here — they have their own SF tab — so this is just the two final-
// weekend games, centered.
function FinalScreen({
  final,
  third,
  node,
}: {
  final: MatchPublic;
  third: MatchPublic | null;
  node: NodeRenderer;
}) {
  return (
    <div className="ko-final-screen">
      <div className="ko-fcard ko-fcur">
        <Crown />
        {node(final)}
      </div>
      {third ? (
        <div className="ko-fcard ko-third">
          <div className="ko-pin">Third place</div>
          {node(third)}
        </div>
      ) : null}
    </div>
  );
}

// ---- Narrow-web semifinals: a small fixed-geometry canvas (scaled to fit, like
// the desktop bracket). Both semis tap a central box at mid-height; the box's top
// branches to "Final" and its bottom to "3rd Place Final" — right-angle bracket
// lines (no diagonals), matching the round screens. ------------------------
const SEMI_CARD_W = 150;
const SEMI_CARD_H = 54;
const SEMI_LABEL_H = 30; // top/bottom label band
const SEMI_BOX_HW = 28; // half-width of the central box
const SEMI_W = 420;
const SEMI_H = 244;

function SemiScreen({
  left,
  right,
  node,
}: {
  left: MatchPublic | undefined;
  right: MatchPublic | undefined;
  node: NodeRenderer;
}) {
  const [ref, scale] = useFitScale(SEMI_W);
  const cx = SEMI_W / 2;
  const midY = SEMI_H / 2;
  const leftInnerX = SEMI_CARD_W; // left card's right (inner) edge
  const rightInnerX = SEMI_W - SEMI_CARD_W; // right card's left (inner) edge
  const boxL = cx - SEMI_BOX_HW;
  const boxR = cx + SEMI_BOX_HW;
  const boxTop = SEMI_LABEL_H + 24;
  const boxBot = SEMI_H - SEMI_LABEL_H - 24;
  const finalY = SEMI_LABEL_H / 2; // Final chip center (top band)
  const thirdY = SEMI_H - SEMI_LABEL_H / 2; // 3rd-place chip center (bottom band)
  const paths = [
    `M ${leftInnerX} ${midY} H ${boxL}`, // left semi → box's left side
    `M ${rightInnerX} ${midY} H ${boxR}`, // right semi → box's right side
    `M ${boxL} ${boxTop} V ${boxBot}`, // box left edge
    `M ${boxR} ${boxTop} V ${boxBot}`, // box right edge
    `M ${boxL} ${boxTop} H ${boxR}`, // box top edge
    `M ${boxL} ${boxBot} H ${boxR}`, // box bottom edge
    `M ${cx} ${boxTop} V ${finalY}`, // top → Final
    `M ${cx} ${boxBot} V ${thirdY}`, // bottom → 3rd-place
  ];
  return (
    <div className="ko-fit" ref={ref}>
      <div className="ko-scale-wrap" style={{ width: SEMI_W * scale, height: SEMI_H * scale }}>
        <div
          className="ko-canvas"
          style={{ width: SEMI_W, height: SEMI_H, transform: `scale(${scale})` }}
        >
          <svg className="ko-lines" width={SEMI_W} height={SEMI_H} aria-hidden>
            {paths.map((d, i) => (
              <path key={i} d={d} fill="none" stroke="var(--fg-muted)" strokeWidth={1.5} />
            ))}
          </svg>
          <div className="ko-bk-label" style={{ left: cx - 90, top: 0, width: 180, height: SEMI_LABEL_H }}>
            <span>Final</span>
          </div>
          <div
            className="ko-bk-label"
            style={{ left: cx - 90, top: SEMI_H - SEMI_LABEL_H, width: 180, height: SEMI_LABEL_H }}
          >
            <span>3rd Place Final</span>
          </div>
          <div
            className="ko-pos"
            style={{ left: 0, top: midY - SEMI_CARD_H / 2, width: SEMI_CARD_W, height: SEMI_CARD_H }}
          >
            {left ? node(left) : null}
          </div>
          <div
            className="ko-pos mirror"
            style={{ left: rightInnerX, top: midY - SEMI_CARD_H / 2, width: SEMI_CARD_W, height: SEMI_CARD_H }}
          >
            {right ? node(right) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- Narrow-web round screen (R32/R16/QF): a real tournament bracket on a
// scaled fixed-geometry canvas. Adjacent cards in each half pair up with an
// elbow (⊐); each pair's merged line runs to a central rail, and the next-round
// label sits on the rail. (SF is the two-outcome diamond; Final is its own
// screen.) ------------------------------------------------------------------
const RS_CARD_W = 144;
const RS_CARD_H = 54;
const RS_ROW_PITCH = 64;
const RS_TOP = 6;
const RS_MERGE_GAP = 18; // card inner edge → the pair's merge column
const RS_CENTER_HALF = 44; // merge column → the central rail
const RS_W = 2 * (RS_CARD_W + RS_MERGE_GAP + RS_CENTER_HALF);
const RS_CENTER_X = RS_W / 2;
const RS_LABEL_H = 30;

const roundCardCenterY = (slot: number) => RS_TOP + slot * RS_ROW_PITCH + RS_CARD_H / 2;

// One half's bracket: pair adjacent cards with a ⊐ elbow, then a stub from each
// pair's midpoint to the central rail. Returns SVG path strings + the pair
// midpoints (used to size the rail).
function halfBracket(count: number, side: 'left' | 'right'): { paths: string[]; mids: number[] } {
  const innerX = side === 'left' ? RS_CARD_W : RS_W - RS_CARD_W;
  const mergeX = side === 'left' ? RS_CARD_W + RS_MERGE_GAP : RS_W - RS_CARD_W - RS_MERGE_GAP;
  const paths: string[] = [];
  const mids: number[] = [];
  for (let a = 0; a < count; a += 2) {
    const ya = roundCardCenterY(a);
    if (a + 1 < count) {
      const yb = roundCardCenterY(a + 1);
      const mid = (ya + yb) / 2;
      mids.push(mid);
      paths.push(`M ${innerX} ${ya} H ${mergeX} V ${yb} H ${innerX}`); // the ⊐ joining the pair
      paths.push(`M ${mergeX} ${mid} H ${RS_CENTER_X}`); // merged line to the rail
    } else {
      mids.push(ya); // lone trailing card (odd count) → straight to the rail
      paths.push(`M ${innerX} ${ya} H ${RS_CENTER_X}`);
    }
  }
  return { paths, mids };
}

function RoundScreen({
  leftCards,
  rightCards,
  label,
  node,
}: {
  leftCards: MatchPublic[];
  rightCards: MatchPublic[];
  label: string;
  node: NodeRenderer;
}) {
  const [ref, scale] = useFitScale(RS_W);
  const rows = Math.max(leftCards.length, rightCards.length);
  const height = RS_TOP * 2 + rows * RS_ROW_PITCH;
  const left = halfBracket(leftCards.length, 'left');
  const right = halfBracket(rightCards.length, 'right');
  const mids = [...left.mids, ...right.mids];
  const railTop = Math.min(...mids);
  const railBot = Math.max(...mids);
  const labelY = (railTop + railBot) / 2;
  const paths = [...left.paths, ...right.paths];
  if (railBot > railTop) paths.push(`M ${RS_CENTER_X} ${railTop} V ${railBot}`); // the central rail

  return (
    <div className="ko-fit" ref={ref}>
      <div className="ko-scale-wrap" style={{ width: RS_W * scale, height: height * scale }}>
        <div className="ko-canvas" style={{ width: RS_W, height, transform: `scale(${scale})` }}>
          <svg className="ko-lines" width={RS_W} height={height} aria-hidden>
            {paths.map((d, i) => (
              <path key={i} d={d} fill="none" stroke="var(--fg-muted)" strokeWidth={1.5} />
            ))}
          </svg>
          <div
            className="ko-bk-label"
            style={{ left: RS_CENTER_X - 80, top: labelY - RS_LABEL_H / 2, width: 160, height: RS_LABEL_H }}
          >
            <span>{label}</span>
          </div>
          {leftCards.map((m, i) => (
            <div
              className="ko-pos"
              key={m.id}
              style={{ left: 0, top: roundCardCenterY(i) - RS_CARD_H / 2, width: RS_CARD_W, height: RS_CARD_H }}
            >
              {node(m)}
            </div>
          ))}
          {rightCards.map((m, i) => (
            <div
              className="ko-pos mirror"
              key={m.id}
              style={{
                left: RS_W - RS_CARD_W,
                top: roundCardCenterY(i) - RS_CARD_H / 2,
                width: RS_CARD_W,
                height: RS_CARD_H,
              }}
            >
              {node(m)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MobileBracket({
  tree,
  node,
}: {
  tree: BracketTree;
  node: NodeRenderer;
}) {
  const { left, right, rank } = useMemo(() => halfLayout(tree), [tree]);

  const tabs = ROUND_TABS.filter((t) =>
    tree.rounds.some((r) => r.stage === t.stage && r.matches.length > 0),
  );
  const [index, setIndex] = useState(0);
  const clamped = Math.min(index, Math.max(0, tabs.length - 1));
  const active = tabs[clamped]?.stage ?? 'round_of_32';
  const matches = tree.rounds.find((r) => r.stage === active)?.matches ?? [];
  const inHalf = (set: Set<string>) =>
    matches.filter((m) => set.has(m.id)).sort((a, b) => (rank.get(a.id) ?? 0) - (rank.get(b.id) ?? 0));

  const go = (delta: number) =>
    setIndex(Math.min(tabs.length - 1, Math.max(0, clamped + delta)));

  // Lightweight swipe: remember the touch-start x and step the pager on release
  // once the horizontal travel clears a small threshold. No gesture library.
  const startX = useRef<number | null>(null);

  return (
    <>
      <div className="ko-pager">
        <button
          type="button"
          className="ko-arrow"
          aria-label="Previous round"
          disabled={clamped === 0}
          onClick={() => go(-1)}
        >
          ‹
        </button>
        <div className="ko-dots">
          {tabs.map((t, i) => (
            <span
              key={t.stage}
              className={cn('ko-dot', i === clamped && 'active')}
              aria-hidden
            />
          ))}
        </div>
        <button
          type="button"
          className="ko-arrow"
          aria-label="Next round"
          disabled={clamped === tabs.length - 1}
          onClick={() => go(1)}
        >
          ›
        </button>
      </div>

      <div
        className="ko-mob"
        onTouchStart={(e) => {
          startX.current = e.touches[0]?.clientX ?? null;
        }}
        onTouchEnd={(e) => {
          if (startX.current === null) return;
          const dx = (e.changedTouches[0]?.clientX ?? startX.current) - startX.current;
          if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
          startX.current = null;
        }}
      >
        {active === 'final' ? (
          <FinalScreen final={matches[0]!} third={tree.thirdPlace} node={node} />
        ) : active === 'semi' ? (
          <SemiScreen left={inHalf(left)[0]} right={inHalf(right)[0]} node={node} />
        ) : (
          <RoundScreen
            leftCards={inHalf(left)}
            rightCards={inHalf(right)}
            label={NEXT_STAGE_LABEL[active] ?? ''}
            node={node}
          />
        )}
      </div>
    </>
  );
}

// ---- Fallback: plain columns if the structure isn't the expected shape ------

function FallbackColumns({ tree, node }: { tree: BracketTree; node: NodeRenderer }) {
  const cols = [
    ...tree.rounds,
    ...(tree.thirdPlace
      ? [{ stage: 'third_place' as MatchStage, matches: [tree.thirdPlace] }]
      : []),
  ];
  return (
    <div className="ko-fit">
      <div className="ko-columns">
        {cols.map((r) => (
          <div className="ko-col" key={r.stage}>
            {r.matches.map((m) => (
              <div className="ko-fcard" key={m.id}>
                {node(m)}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export function KnockoutBracket({ groups, matches }: { groups: Group[]; matches: MatchPublic[] }) {
  const resolved = useMemo(() => resolveBracket(matches, groups), [matches, groups]);
  const liveByTeam = useMemo(() => liveMatchesByTeam(matches), [matches]);
  const tree = useMemo(() => buildBracketTree(matches, groups), [matches, groups]);
  const byId = useMemo(() => new Map(matches.map((m) => [m.id, m])), [matches]);
  const layout = useMemo(() => computeDesktopLayout(tree, byId), [tree, byId]);
  // Reserve the wide two-sided bracket for a full 1080p monitor (≥ 1920px wide);
  // anything narrower falls back to the mobile pager.
  const isMobile = useMediaQuery('(max-width: 1919px)');

  // While ≥1 group match is still unfinished the bracket only PROJECTS its R32
  // entrants from the live standings, so each projected slot gets a certainty
  // badge. Once every group match is final there's nothing to project — drop
  // the badges (lockedIds === null) rather than mark them all locked.
  const showLocks = useMemo(
    () => matches.some((m) => m.stage === 'group' && m.status !== 'finished'),
    [matches],
  );
  const lockedIds = useMemo(
    () => (showLocks ? lockedR32SlotTeamIds(groups, matches) : null),
    [showLocks, groups, matches],
  );

  const node: NodeRenderer = (m) => {
    const r = resolved.get(m.id) ?? { team1: m.team1 ?? null, team2: m.team2 ?? null };
    return <Node m={m} team1={r.team1} team2={r.team2} liveByTeam={liveByTeam} lockedIds={lockedIds} />;
  };

  if (!layout.ok) return <FallbackColumns tree={tree} node={node} />;
  if (isMobile) return <MobileBracket tree={tree} node={node} />;
  return <DesktopBracket layout={layout} node={node} />;
}

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false,
  );
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mq.addEventListener('change', handler);
    setMatches(mq.matches);
    return () => mq.removeEventListener('change', handler);
  }, [query]);
  return matches;
}
